/**
 * Onboarding Slice - Redux slice for onboarding state management
 * Manages user onboarding progress, events, and eligibility
 */

import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import {
  OnboardingState,
  OnboardingStateResponse,
  OnboardingEvent,
  OnboardingEventsParams,
} from '../../types/onboarding';
import { logger } from '../../utils/logger';
import type { AppDispatch, RootState } from '../index';

// ============================================================================
// State Interface
// ============================================================================

export interface OnboardingSliceState {
  state: OnboardingStateResponse | null;
  loading: boolean;
  error: string | null;
  events: OnboardingEvent[];
  eventsLoading: boolean;
  eventsError: string | null;
  operationSuccess: boolean;
  operationLoading: boolean;
  operationError: string | null;
}

// Initial state
const initialState: OnboardingSliceState = {
  state: null,
  loading: false,
  error: null,
  events: [],
  eventsLoading: false,
  eventsError: null,
  operationSuccess: false,
  operationLoading: false,
  operationError: null,
};

// ============================================================================
// Slice Definition
// ============================================================================

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error message
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Set onboarding state
    setOnboardingState: (state, action: PayloadAction<OnboardingStateResponse>) => {
      state.state = action.payload;
      state.loading = false;
      state.error = null;
    },

    // Set events list
    setEvents: (state, action: PayloadAction<OnboardingEvent[]>) => {
      state.events = action.payload;
      state.eventsLoading = false;
      state.eventsError = null;
    },

    // Set events loading state
    setEventsLoading: (state, action: PayloadAction<boolean>) => {
      state.eventsLoading = action.payload;
    },

    // Set events error
    setEventsError: (state, action: PayloadAction<string | null>) => {
      state.eventsError = action.payload;
      state.eventsLoading = false;
    },

    // Set operation loading state
    setOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.operationLoading = action.payload;
    },

    // Set operation success
    setOperationSuccess: (state, action: PayloadAction<boolean>) => {
      state.operationSuccess = action.payload;
      state.operationLoading = false;
      state.operationError = null;
    },

    // Set operation error
    setOperationError: (state, action: PayloadAction<string | null>) => {
      state.operationError = action.payload;
      state.operationLoading = false;
    },

    // Clear operation state
    clearOperationState: (state) => {
      state.operationSuccess = false;
      state.operationError = null;
    },

    // Reset onboarding state to initial
    resetOnboardingState: () => initialState,
  },
});

// Export actions
export const {
  setLoading,
  setError,
  clearError,
  setOnboardingState,
  setEvents,
  setEventsLoading,
  setEventsError,
  setOperationLoading,
  setOperationSuccess,
  setOperationError,
  clearOperationState,
  resetOnboardingState,
} = onboardingSlice.actions;

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch onboarding state from API
 */
export const fetchOnboardingState = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      logger.debug('Fetching onboarding state...');

      const onboardingApi = await import('../../services/onboarding');
      const response = await onboardingApi.default.getOnboardingState();

      logger.debug('Onboarding state fetched successfully', response);
      dispatch(setOnboardingState(response));

      return { success: true, data: response };
    } catch (error) {
      logger.error('Error fetching onboarding state', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch onboarding state';
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Log onboarding event
 */
export const logOnboardingEvent = (
  eventType: string,
  metadata?: Record<string, any>
) => {
  return async (dispatch: AppDispatch) => {
    try {
      logger.debug('Logging onboarding event', { eventType, metadata });

      const onboardingApi = await import('../../services/onboarding');
      const response = await onboardingApi.default.logOnboardingEvent(eventType, metadata);

      logger.debug('Onboarding event logged successfully', response);

      return { success: true, message: response.message };
    } catch (error) {
      logger.error('Error logging onboarding event', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to log onboarding event';
      dispatch(setError(errorMessage));
      throw error;
    }
  };
};

/**
 * Mark onboarding as completed
 */
export const markOnboardingCompleted = (finalState?: OnboardingState) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setOperationLoading(true));
      dispatch(clearOperationState());

      logger.debug('Marking onboarding as completed', { finalState });

      const onboardingApi = await import('../../services/onboarding');
      const response = await onboardingApi.default.markOnboardingCompleted(finalState);

      logger.debug('Onboarding marked as completed', response);
      dispatch(setOperationSuccess(true));

      // Refresh onboarding state after completion
      dispatch(fetchOnboardingState());

      return { success: true, message: response.message };
    } catch (error) {
      logger.error('Error marking onboarding as completed', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete onboarding';
      dispatch(setOperationError(errorMessage));
      throw error;
    } finally {
      dispatch(setOperationLoading(false));
    }
  };
};

/**
 * Mark onboarding as skipped
 */
export const markOnboardingSkipped = (reason?: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setOperationLoading(true));
      dispatch(clearOperationState());

      logger.debug('Marking onboarding as skipped', { reason });

      const onboardingApi = await import('../../services/onboarding');
      const response = await onboardingApi.default.markOnboardingSkipped(reason);

      logger.debug('Onboarding marked as skipped', response);
      dispatch(setOperationSuccess(true));

      // Refresh onboarding state after skip
      dispatch(fetchOnboardingState());

      return { success: true, message: response.message };
    } catch (error) {
      logger.error('Error marking onboarding as skipped', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to skip onboarding';
      dispatch(setOperationError(errorMessage));
      throw error;
    } finally {
      dispatch(setOperationLoading(false));
    }
  };
};

/**
 * Restart onboarding (self-service) - clears skip/complete so the
 * checklist and welcome modal become active again
 */
export const restartOnboarding = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setOperationLoading(true));
      dispatch(clearOperationState());

      logger.debug('Restarting onboarding...');

      const onboardingApi = await import('../../services/onboarding');
      const response = await onboardingApi.default.restartOnboarding();

      logger.debug('Onboarding restarted', response);
      dispatch(setOnboardingState(response));
      dispatch(setOperationSuccess(true));

      return { success: true, data: response };
    } catch (error) {
      logger.error('Error restarting onboarding', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to restart onboarding';
      dispatch(setOperationError(errorMessage));
      throw error;
    } finally {
      dispatch(setOperationLoading(false));
    }
  };
};

/**
 * Fetch onboarding events
 */
export const fetchOnboardingEvents = (params?: OnboardingEventsParams) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEventsLoading(true));
      dispatch(setEventsError(null));

      logger.debug('Fetching onboarding events', params);

      const onboardingApi = await import('../../services/onboarding');
      const response = await onboardingApi.default.getOnboardingEvents(params);

      logger.debug('Onboarding events fetched successfully', response);
      dispatch(setEvents(response.events));

      return { success: true, events: response.events, total: response.total };
    } catch (error) {
      logger.error('Error fetching onboarding events', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch onboarding events';
      dispatch(setEventsError(errorMessage));
      throw error;
    } finally {
      dispatch(setEventsLoading(false));
    }
  };
};

/**
 * Reset user onboarding (admin only)
 */
export const resetUserOnboarding = (userId: number, reason?: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setOperationLoading(true));
      dispatch(clearOperationState());

      logger.debug('Resetting user onboarding', { userId, reason });

      const onboardingApi = await import('../../services/onboarding');
      const response = await onboardingApi.default.resetUserOnboarding(userId, reason);

      logger.debug('User onboarding reset successfully', response);
      dispatch(setOperationSuccess(true));

      return { success: true, message: response.message };
    } catch (error) {
      logger.error('Error resetting user onboarding', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset user onboarding';
      dispatch(setOperationError(errorMessage));
      throw error;
    } finally {
      dispatch(setOperationLoading(false));
    }
  };
};

// Export reducer
export default onboardingSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

/**
 * Base selectors
 */
export const selectOnboardingState = (state: RootState) => state.onboarding.state;
export const selectOnboardingLoading = (state: RootState) => state.onboarding.loading;
export const selectOnboardingError = (state: RootState) => state.onboarding.error;
export const selectOnboardingEvents = (state: RootState) => state.onboarding.events;
export const selectOnboardingEventsLoading = (state: RootState) => state.onboarding.eventsLoading;
export const selectOnboardingEventsError = (state: RootState) => state.onboarding.eventsError;
export const selectOnboardingOperationSuccess = (state: RootState) => state.onboarding.operationSuccess;
export const selectOnboardingOperationLoading = (state: RootState) => state.onboarding.operationLoading;
export const selectOnboardingOperationError = (state: RootState) => state.onboarding.operationError;

/**
 * Memoized selectors for derived data
 */

// Check if onboarding is completed. Truthiness check on purpose: when the
// state hasn't been fetched yet, `state?.onboarding_completed_at` is
// undefined and a `!== null` comparison would wrongly report completed.
export const selectIsOnboardingCompleted = createSelector(
  [selectOnboardingState],
  (state) => Boolean(state?.onboarding_completed_at)
);

// Check if onboarding was skipped (see completed-selector note on truthiness)
export const selectIsOnboardingSkipped = createSelector(
  [selectOnboardingState],
  (state) => Boolean(state?.onboarding_skipped_at)
);

